import type { FC } from "react";
import { memo } from "react";

import { TrafficShare } from "app/components/React/Table/cells";
import { DefaultCellHeader } from "app/components/React/Table/headerCells";
import { LinkCell } from "pro-features/components/Workspace/TableCells/LinkCell";
import { i18nFilter } from "app/filters/i18nFilter";
import { EmptyState } from "app/pages/website-analysis/traffic-sources/display-ads/common/EmptyState";
import {
    HeaderWithMarker,
    StyledCellCategory,
    StyledTable,
} from "app/pages/website-analysis/traffic-sources/display-ads/publishers/common/StyledComponents";
import type { IDistributionItemTable } from "app/pages/website-analysis/traffic-sources/display-ads/publishers/compare/PublishersTableCompare";

interface IDistributionByNetworksProps {
    networksDistribution: IDistributionItemTable;
    domains: { color: string; name: string }[];
    getLink: (key: string, value: string) => string;
    topNetworks: string[];
    thresholdForOthers?: number;
    emptyStateKey?: string;
}
const i18n = i18nFilter();
const DistributionByNetworksInner: FC<IDistributionByNetworksProps> = ({
    domains,
    emptyStateKey = "display.ads.publishers.empty.state.title",
    getLink,
    networksDistribution,
    thresholdForOthers = 5,
    topNetworks,
}) => {
    const transformData = () => {
        const topSixNetworks = topNetworks?.slice(0, 6);
        const data = topSixNetworks.map((network) => {
            const row = {
                network: network,
            };

            domains.forEach((domain) => {
                row[domain.name] = [networksDistribution[domain.name]?.find((n) => n.Name === network)?.Value];
            });

            return row;
        });

        if (topNetworks.length > thresholdForOthers) {
            const row = {
                network: "Others",
            };
            domains.forEach((domain) => {
                let othersSum = data.reduce((acc, val) => {
                    if (val[domain.name][0]) {
                        acc += val[domain.name][0];
                    }
                    return acc;
                }, 0);
                othersSum = Number(parseFloat(othersSum.toString()).toFixed(4));
                row[domain.name] = othersSum <= 0 ? null : 1 - othersSum;
            });

            return [...data, row];
        } else {
            return data;
        }
    };

    const createColumns = (domains, getLink) => {
        const columns: any[] = [
            {
                field: "network",
                displayName: i18n("display.ads.publishers.categorydistribution.column.networks"),
                headerComponent: DefaultCellHeader,
                cellComponent: (props) => {
                    const { value } = props;
                    if (value === "Others") {
                        return <div>{value}</div>;
                    } else {
                        return (
                            <StyledCellCategory>
                                <LinkCell
                                    href={getLink("network", value)}
                                    value={value}
                                    title={value}
                                />
                            </StyledCellCategory>
                        );
                    }
                },
                visible: true,
                minWidth: 250,
            },
        ];
        domains.forEach((domain) => {
            const newColumn = {
                field: domain.name,
                displayName: domain.name,
                headerComponent: HeaderWithMarker(domain),
                cellComponent: TrafficShare,
                minWidth: 100,
                maxWidth: 300,
                visible: true,
                innerColor: domain.color,
            };
            columns.push(newColumn);
        });
        return columns;
    };

    const isNotEmptyState = () => {
        let isEmptyState = true;
        if (networksDistribution) {
            const values = Object.values(networksDistribution);
            let i = 0;
            while (isEmptyState && i < values.length) {
                if (values[i] && values[i].length > 0) {
                    isEmptyState = false;
                }
                i++;
            }
        }
        return !isEmptyState && domains && topNetworks && topNetworks.length > 0;
    };

    return isNotEmptyState() ? (
        <StyledTable
            className="MiniFlexTable"
            data={transformData()}
            columns={createColumns(domains, getLink)}
        />
    ) : (
        <EmptyState titleKey={emptyStateKey} />
    );
};

const propsAreEqual = (prevProps, nextProps) => {
    const { networksDistribution, topNetworks } = prevProps;
    const { networksDistribution: nextNetworksDistribution, topNetworks: nextTopNetworks } = nextProps;
    return networksDistribution === nextNetworksDistribution && nextTopNetworks === topNetworks;
};

export const DistributionByNetworks = memo(DistributionByNetworksInner, propsAreEqual);
